import React, { createContext, useContext, useMemo, useSyncExternalStore } from "react";
import { useSsoSession } from "../hooks/useSsoSession";
import {
  clearAdminToken,
  getAdminToken,
  setAdminToken,
  subscribeAdminToken,
} from "../auth/adminTokenStore";
import { INTERNAL_APP_HEADER } from "../api/client";
import stytchClient from "../services/stytchClient";

const BASE_URL = process.env.REACT_APP_BE_URL as string;

type AdminAuthContextValue = {
  setToken: (token: string, remember?: boolean) => void;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
};

const AdminAuthContext = createContext<AdminAuthContextValue | undefined>(undefined);

// Two independent sessions: the legacy admin token (adminTokenStore, sent as the
// admin-token header) and SSO (HttpOnly cookie; useSsoSession only flags it).
export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const hasSsoSession = useSsoSession();
  const adminToken = useSyncExternalStore(subscribeAdminToken, getAdminToken);

  const handleSetToken = (newToken: string, remember?: boolean) => {
    setAdminToken(newToken, { remember });
  };

  const handleLogout = async () => {
    // Revoke the server session and clear the HttpOnly admin-auth-token cookie — JS
    // can't touch it, so only the backend can. Harmless for admin-token users.
    // Local state is cleared only after both settle, so the UI never shows
    // "logged out" while the server session is still live. No keepalive: the
    // custom app header needs a CORS preflight, which keepalive requests may
    // not get. The timeout keeps a hung backend from blocking logout forever.
    await Promise.allSettled([
      fetch(`${BASE_URL}/logout`, {
        method: "POST",
        headers: INTERNAL_APP_HEADER,
        credentials: "include",
        signal: AbortSignal.timeout(5000),
      }),
      stytchClient?.session.revoke(),
    ]);
    clearAdminToken();
  };

  const value = useMemo(
    () => ({
      setToken: handleSetToken,
      logout: handleLogout,
      isAuthenticated: Boolean(adminToken) || hasSsoSession,
    }),
    [adminToken, hasSsoSession]
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
};

export const useAdminAuth = (): AdminAuthContextValue => {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) {
    throw new Error("useAdminAuth must be used within AdminAuthProvider");
  }
  return ctx;
};
